import React, { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, GradientTexture } from '@react-three/drei';
import * as THREE from 'three';

const HeadModel = ({ scanProgress, scanActive }) => {
  const group = useRef();
  const scanRef = useRef();
  const textureRef = useRef();
  
  // For holographic scan effect
  const [scanPosition, setScanPosition] = useState(-1.5);
  const [dataPoints, setDataPoints] = useState([]);
  const [textureLoaded, setTextureLoaded] = useState(false);
  
  // For scan line color
  const [scanColor, setScanColor] = useState(new THREE.Color('#00ffff'));
  const colors = ['#00ffff', '#ff00ff', '#9966cc', '#ff6600', '#66ff99', '#ccff00'];
  
  // Load the PNG texture for the sphere
  useEffect(() => {
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('/Untitled-3.png', (texture) => {
      textureRef.current = texture;
      setTextureLoaded(true);
    });
  }, []);
  
  // Generate random data points when scan becomes active
  useEffect(() => {
    if (scanActive) {
      const points = [];
      for (let i = 0; i < 20; i++) {
        points.push({
          position: [
            (Math.random() - 0.5) * 2.5,
            (Math.random() - 0.5) * 2.5,
            (Math.random() - 0.5) * 2.5,
          ],
          color: colors[Math.floor(Math.random() * colors.length)],
          scale: Math.random() * 0.08 + 0.02,
          speed: Math.random() * 0.02 + 0.01,
          visible: false,
          appearAt: Math.floor(Math.random() * 50) + 10
        });
      }
      setDataPoints(points);
    }
  }, [scanActive]);
  
  // Animation for scan line
  useFrame((state, delta) => {
    if (group.current) {
      // Subtle floating animation for head
      group.current.rotation.y += 0.005;
      group.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
    
    // Update data points
    if (scanActive && dataPoints.length > 0) {
      dataPoints.forEach((point, index) => {
        if (scanProgress >= point.appearAt && !point.visible) {
          setDataPoints(prev => {
            const updated = [...prev];
            updated[index].visible = true;
            return updated;
          });
        }
      });
    }
    
    // Scan line movement when scan is active
    if (scanActive && scanRef.current) {
      // Move scan plane up and down
      if (scanProgress < 100) {
        // Calculate scan position based on sin wave for up/down movement
        const newPosition = Math.sin(state.clock.elapsedTime * 2) * 1.5;
        setScanPosition(newPosition);
        
        // Change scan color based on time
        const colorIndex = Math.floor((state.clock.elapsedTime * 2) % colors.length);
        setScanColor(new THREE.Color(colors[colorIndex]));
      } else {
        // When scan completes, make scan line surround the entire head
        scanRef.current.scale.set(2, 2, 2);
        scanRef.current.material.opacity = 0.8 + Math.sin(state.clock.elapsedTime * 5) * 0.2;
      }
    }
  });

  return (
    <group ref={group}>
      {textureLoaded && (
        <>
          {/* Larger sphere with PNG texture mapped onto it */}
          <mesh>
            <sphereGeometry args={[1.5, 32, 32]} />
            <meshStandardMaterial 
              map={textureRef.current}
              metalness={0.4}
              roughness={0.6}
              emissive="#333333"
              emissiveIntensity={0.1}
            />
          </mesh>
          
          {/* No wireframe overlays as requested */}
        </>
      )}
      
      {/* Holographic scan line */}
      <mesh 
        ref={scanRef}
        position={[0, scanPosition, 0]} 
        rotation={[Math.PI / 2, 0, 0]}
      >
        <planeGeometry args={[3, 0.1]} />
        <meshBasicMaterial 
          transparent={true}
          opacity={0.8}
          side={THREE.DoubleSide}
          color={scanColor}
        />
      </mesh>
      
      {/* Secondary scan effect */}
      <mesh 
        position={[0, scanPosition * 0.8, 0]} 
        rotation={[Math.PI / 2, 0, 0]}
        scale={[0.95, 0.5, 0.95]}
      >
        <planeGeometry args={[3, 0.05]} />
        <meshBasicMaterial 
          transparent={true}
          opacity={0.4}
          side={THREE.DoubleSide}
        >
          <GradientTexture
            stops={[0, 1]}
            colors={['#ff00ff', 'transparent']}
            size={1024}
          />
        </meshBasicMaterial>
      </mesh>
      
      {/* Data points that appear during scan */}
      {dataPoints.map((point, i) => (
        point.visible && (
          <mesh 
            key={i}
            position={point.position}
            scale={point.scale}
          >
            <sphereGeometry />
            <meshBasicMaterial 
              color={point.color} 
              transparent={true} 
              opacity={0.7}
            />
          </mesh>
        )
      ))}
      
      {/* Additional geometric elements for visual interest */}
      <mesh position={[0, 0, 0]} rotation={[0, 0, 0]}>
        <octahedronGeometry args={[2.1, 0]} />
        <meshBasicMaterial 
          color="#1A82FF" 
          wireframe={true} 
          transparent={true}
          opacity={0.1}
        />
      </mesh>
    </group>
  );
};

export default HeadModel;